import { CUSTOMER_LEVEL, IErrorApiResponse } from '@wyz/types';
import { useQuery } from '@wyz/utils';
import { useManageUsers } from '~/common/api';
import { useApplicationContext } from '~/common/context';
import { ICustomerOption } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';

/** Enough to tell "no customer" from "a single one" from "several ones". */
const SINGLE_CUSTOMER_PROBE_LIMIT = 2;

/**
 * Searches the customers of the current user server side. The number of results
 * is capped by the API (see DEFAULT_CUSTOMER_AUTOCOMPLETE_LIMIT): the whole
 * list can hold hundreds of thousands of customers and must never be fetched.
 */
export const useMyCustomersAutocomplete = ({
  containerEligibleOnly = false,
}: { containerEligibleOnly?: boolean } = {}) => {
  const { auth } = useApplicationContext();
  const userId = auth.userId as string;
  const { customersByUserAutocomplete } = useManageUsers({
    customersByUserAutocomplete: { enabled: true },
  });

  return async (
    query: string,
    options: { limit?: number; signal?: AbortSignal } = {},
  ): Promise<Array<ICustomerOption>> => {
    const customers = await customersByUserAutocomplete(query, {
      userId,
      minLevel: CUSTOMER_LEVEL.N1,
      limit: options.limit,
      signal: options.signal,
      containerEligibleOnly,
    });

    return customers.map((customer) => ({
      ...customer,
      label: `${customer.name} (${customer.code})`,
    }));
  };
};

/**
 * Fetches at most {@link SINGLE_CUSTOMER_PROBE_LIMIT} customers of the current
 * user, to tell the single customer case from the several ones case without
 * loading the whole list.
 */
export const useMyCustomersProbe = ({
  containerEligibleOnly = false,
}: { containerEligibleOnly?: boolean } = {}) => {
  const { auth } = useApplicationContext();
  const userId = auth.userId as string;
  const fetchCustomers = useMyCustomersAutocomplete({ containerEligibleOnly });

  return useQuery<Array<ICustomerOption>, IErrorApiResponse>({
    queryKey: [
      ...(containerEligibleOnly
        ? ConnectQueryKeys.users.containerEligibleCustomersByUser(
            userId,
            CUSTOMER_LEVEL.N1,
          )
        : ConnectQueryKeys.users.customersByUser(userId, CUSTOMER_LEVEL.N1)),
      'single-customer-probe',
    ],
    enabled: Boolean(userId),
    refetchOnMount: true,
    queryFn: () => fetchCustomers('', { limit: SINGLE_CUSTOMER_PROBE_LIMIT }),
  });
};

type UseMyCustomersArgs = {
  minLevel?: number;
  parentIds?: string[];
  enabled?: boolean;
};

export const useMyCustomers = ({
  minLevel = CUSTOMER_LEVEL.N1,
  parentIds = [],
  enabled = true,
}: UseMyCustomersArgs = {}) => {
  const { auth } = useApplicationContext();
  const customerQuery = useManageUsers({
    getCustomersByUser: {
      args: { id: auth.userId as string, minLevel, parentIds },
      enabled: enabled,
      refetchOnMount: true,
    },
  }).getCustomersByUser;

  return customerQuery;
};
